import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Roles y permisos»: the matrix, the permission templates and the history of changes in one answer. The custom roles of
 * EVERY organisation travel together in the matrix, which is why a role may carry `tenantId` and `tenantName`. Each
 * schema mirrors the interface the service returns (`PermissionCategory`, `RolePermissions`, `PermissionTemplate`,
 * `AuditLogEntry` with its date as text); the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-permissions-matrix-screen.type-assertions.ts` tie them in both
 * directions. The names carry the prefix `matrix` because `permissionSchema` already exists in `@bat/shared`.
 */

export const PERMISSIONS_AUDIT_LIMIT = 50

export const matrixPermissionSchema = z.strictObject({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().optional(),
  category: z.string(),
  isSystem: z.boolean(),
})

export const matrixCategorySchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().optional(),
  displayOrder: z.number(),
  permissions: z.array(matrixPermissionSchema),
})

export const matrixRoleSchema = z.strictObject({
  roleId: z.string(),
  roleName: z.string(),
  roleType: z.enum(['built_in', 'custom']),
  permissions: z.record(z.string(), z.boolean()),
  tenantId: z.string().optional(),
  tenantName: z.string().optional(),
})

export const matrixTemplateSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  permissions: z.array(z.string()),
  isSystem: z.boolean(),
})

export const matrixAuditRowSchema = z.strictObject({
  id: z.string(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  changes: z.record(z.string(), z.strictObject({ from: z.boolean().nullable(), to: z.boolean() })),
  performedBy: z.string(),
  performedByName: z.string().optional(),
  performedAt: z.string(),
})

export const adminPermissionsMatrixScreen = defineScreen({
  id: 'admin.permissionsMatrix',
  path: '/api/screens/admin/permissions-matrix',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    matrix: screenSection(
      z.strictObject({
        categories: z.array(matrixCategorySchema),
        roles: z.array(matrixRoleSchema),
      }),
    ),
    templates: screenSection(z.array(matrixTemplateSchema)),
    audit: screenSection(z.array(matrixAuditRowSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.permissions'],
  // `statements` grows with the roles: one per built-in role and one per custom role (matrix-retriever N+1)
  budget: { requests: 1, statements: 24, bytes: 250_000, p95Ms: 400 },
  summary:
    'Roles and permissions: the matrix of categories and roles, the permission templates and the last 50 permission changes',
})
export type MatrixCategory = z.infer<typeof matrixCategorySchema>
export type MatrixRole = z.infer<typeof matrixRoleSchema>
export type MatrixTemplate = z.infer<typeof matrixTemplateSchema>
export type MatrixAuditRow = z.infer<typeof matrixAuditRowSchema>
export type AdminPermissionsMatrixData = ScreenData<typeof adminPermissionsMatrixScreen>
